'use client'

import { GoogleMaps } from 'maps/components/commons/GoogleMaps/GoogleMaps'
import { MarkerCard } from 'maps/components/commons/MarkerCard/MarkerCard'
import { useExpandedStore } from 'maps/hooks/useExpandedStore/useExpandedStore'
import type { Store } from 'maps/types'
import { URL_GOOGLE_MAPS_API } from 'maps/utils/mapOptions/constants'
import dynamic from 'next/dynamic'
import Script from 'next/script'
import { type RefObject, useEffect, useRef, useState } from 'react'

const MapError = dynamic(() =>
	import('maps/components/MapError/MapError').then((mod) => mod.MapError)
)

interface MapsProps {
	readonly onConfirm?: (store: Store) => void
	readonly onError?: () => void
	readonly className?: string
	readonly showCard?: boolean
}

export function Maps({ showCard, onConfirm, onError, className }: MapsProps) {
	const [loadMap, setLoadMap] = useState(false)
	const [hasError, setHasError] = useState(false)
	const cardRef: RefObject<HTMLDivElement | null> = useRef(null)
	const lastFocusedElement: RefObject<HTMLElement | null> = useRef(null)

	const { expanded } = useExpandedStore()

	const handleScript = () => {
		setLoadMap(true)
	}

	const handleError = () => {
		setHasError(true)
		setLoadMap(false)
	}

	useEffect(() => {
		if (expanded) {
			lastFocusedElement.current = document.activeElement as HTMLElement
			cardRef.current?.focus()
		} else {
			lastFocusedElement.current?.focus()
		}
	}, [expanded])

	return (
		<>
			<Script
				src={URL_GOOGLE_MAPS_API}
				onReady={handleScript}
				onLoad={handleScript}
				onError={handleError}
			/>
			{loadMap && (
				<GoogleMaps className={className}>
					{showCard && <MarkerCard ref={cardRef} onConfirm={onConfirm} />}
				</GoogleMaps>
			)}
			{hasError && <MapError onError={onError} />}
		</>
	)
}
